import React, { useState } from 'react';
import { useSelector } from "react-redux";
import { Paper, Typography, TextField, Box, Collapse, Grid, Divider, Switch, FormControlLabel } from '@mui/material';
import { hotelCostCalculation, calculateCarCost, calculateSingleHotelCostWithBreakdown } from '../createInquiryCalculation';

const CostEstimateCard = ({
    selectedPackage = null,
    cost,
    handleCostChange,
    hotelSelections = {},
    allHotels = [],
    stayInfo = {},
    tripDetails = {},
    configData = {},
    carSeason = '',
    hotelSeason = '',
    currentMargin,
    setCurrentMargin,
    carSelections = {},
    isB2B,
    setIsB2B
}) => {
    const [showBreakdown, setShowBreakdown] = useState(false);
    const [showHotelBreakdown, setShowHotelBreakdown] = useState(false);
    const [tempCost, setTempCost] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    
    const { user } = useSelector(state => state.tokens);
    const totalDays = (parseInt(tripDetails.duration) || 0) + 1;
    const carDetails = tripDetails?.car_details || [];

    const carTotal = calculateCarCost(configData, carSeason, tripDetails, carSelections);
    const hotelTotal = hotelCostCalculation(hotelSelections, allHotels, hotelSeason, tripDetails, stayInfo);
    const extrasCost = (tripDetails.optional_extras || []).reduce((acc, e) => acc + ((e.price || 0) * (e.count || 1)), 0);
    const extrasCostForCalc = (tripDetails.optional_extras || []).reduce((acc, e) => {
        return acc + (e.include_in_cost !== false ? ((e.price || 0) * (e.count || 1)) : 0);
    }, 0);

    React.useEffect(() => {
        setTempCost(null);
    }, [carTotal, hotelTotal, extrasCostForCalc]);

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Cost Estimate
                </Typography>
                <FormControlLabel
                    control={<Switch checked={!!isB2B} onChange={(e) => {
                        const newIsB2B = e.target.checked;
                        setIsB2B(newIsB2B);
                        const isSandakphu = tripDetails?.location === 'Sandakphu';
                        const newMinMargin = user?.permission === 'Admin' ? 0 : (isSandakphu ? 0 : (carSeason === 'off_season_price' ? (newIsB2B ? 8 : 15) : (newIsB2B ? 12 : 20)));
                        if (typeof currentMargin === 'number' && currentMargin < newMinMargin) {
                            setCurrentMargin(newMinMargin);
                            setTempCost(null);
                        }
                    }} />}
                    label="B2B Pricing Mode"
                />
            </Box>

            {(() => {
                const getEffectiveMargin = (manualCostStr) => {
                    const manualCost = parseFloat(manualCostStr);
                    if (!manualCost) return null;
                    if (tripDetails?.location !== 'Sandakphu') {
                        const baseCost = (parseFloat(hotelTotal) || 0) + (parseFloat(carTotal) || 0);
                        if (baseCost > 0) return ((manualCost - baseCost - extrasCostForCalc) / baseCost) * 100;
                    } else {
                        const head_count = (parseInt(tripDetails.pax) || 0) + (parseInt(tripDetails.kids_above_5) || 0);
                        const duration = parseInt(tripDetails.duration) || 0;
                        let subtotal = 0;
                        if (selectedPackage?.label?.includes("Land Rover")) {
                            subtotal = ((parseFloat(hotelTotal) || 0) * head_count) + (parseFloat(carTotal) || 0);
                        } else {
                            const singleCost = selectedPackage?.details?.cost?.singleCost || 0;
                            subtotal = singleCost * head_count;
                        }
                        if (subtotal > 0 && duration > 0) return ((subtotal - ((manualCost - extrasCostForCalc) / duration)) / subtotal) * 100;
                    }
                    return null;
                };

                const effMargin = getEffectiveMargin(tempCost);
                const isSandakphu = tripDetails?.location === 'Sandakphu';
                const b2bMinMargin = carSeason === 'off_season_price' ? 8 : 12;
                const regularMinMargin = carSeason === 'off_season_price' ? 15 : 20;
                const baseMinMargin = isB2B ? b2bMinMargin : regularMinMargin;
                const minMargin = user?.permission === 'Admin' ? 0 : (isSandakphu ? 0 : baseMinMargin);

                const applyTempCost = () => {
                    if (effMargin === null) {
                        setTempCost(null);
                        setErrorMsg('');
                        return;
                    }
                    if (!isSandakphu && effMargin < minMargin) {
                        const baseCost = (parseFloat(hotelTotal) || 0) + (parseFloat(carTotal) || 0);
                        const minAllowedCost = Math.ceil(baseCost * (1 + minMargin / 100)) + extrasCostForCalc;
                        setErrorMsg(`Cost cannot be below ${minMargin}% margin (Min: ₹${minAllowedCost})`);
                        setCurrentMargin(minMargin);
                        setTempCost(null);
                    } else {
                        setErrorMsg('');
                        setCurrentMargin(effMargin);
                    }
                };

                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                            <Box sx={{ flex: 1, p: 2, borderRadius: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)' }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Total Base Cost
                                </Typography>
                                <Typography variant="h5" color="text.primary" fontWeight={700} sx={{ mt: 0.5 }}>
                                    ₹{((parseFloat(hotelTotal) || 0) + (parseFloat(carTotal) || 0) + extrasCostForCalc).toFixed(0)}
                                </Typography>
                            </Box>
                            {!isSandakphu && (
                                <Box sx={{ flex: 1, p: 2, borderRadius: 2, bgcolor: '#fff4e5', border: '1px solid #ffe0b2', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.02)' }}>
                                    <Typography variant="caption" color="#ed6c02" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        {minMargin}% Margin Cost
                                    </Typography>
                                    <Typography variant="h5" color="#e65100" fontWeight={700} sx={{ mt: 0.5 }}>
                                        ₹{Math.ceil(((parseFloat(hotelTotal) || 0) + (parseFloat(carTotal) || 0)) * (1 + minMargin / 100) + extrasCostForCalc)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        
                        <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', display: 'flex', flexDirection: 'column', gap: 2, boxShadow: '0 2px 8px rgba(46, 125, 50, 0.1)' }}>
                            <Typography variant="subtitle2" color="#2e7d32" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {isB2B ? 'B2B Final Quoted Cost' : 'Final Quoted Cost'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <TextField
                                    fullWidth
                                    size="medium"
                                    label="Amount (Roundup)"
                                    type="number"
                                    value={tempCost ?? cost}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setTempCost(val);
                                        setErrorMsg('');
                                        const calcMargin = getEffectiveMargin(val);
                                        if (calcMargin !== null && calcMargin !== undefined) {
                                            if (isSandakphu) {
                                                if (calcMargin >= 0) setCurrentMargin(calcMargin);
                                            } else {
                                                if (calcMargin >= minMargin) setCurrentMargin(calcMargin);
                                            }
                                        }
                                    }}
                                    onBlur={applyTempCost}
                                    onKeyDown={(e) => e.key === 'Enter' && applyTempCost()}
                                    error={!!errorMsg}
                                    helperText={errorMsg || (effMargin !== null ? `Effective Margin: ${effMargin.toFixed(2)}%` : 'Type & press Enter to override')}
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 1, color: '#2e7d32', fontWeight: 600 }}>₹</Typography>,
                                        sx: { bgcolor: '#fff', borderRadius: 1.5 }
                                    }}
                                />
                                {isSandakphu ? (
                                    <TextField
                                        size="medium"
                                        label="Discount (%)"
                                        type="number"
                                        value={typeof currentMargin === 'number' && !Number.isInteger(currentMargin) ? Number(currentMargin.toFixed(2)) : currentMargin}
                                        onChange={(e) => {
                                            setTempCost(null);
                                            const val = e.target.value;
                                            if (val !== '' && parseFloat(val) < 0) {
                                                setCurrentMargin(0);
                                            } else {
                                                setCurrentMargin(val);
                                            }
                                        }}
                                        sx={{ width: 140 }}
                                        InputProps={{
                                            sx: { bgcolor: '#fff', borderRadius: 1.5 }
                                        }}
                                    />
                                ) : (
                                    <TextField
                                        size="medium"
                                        label="Margin (%)"
                                        type="number"
                                        value={typeof currentMargin === 'number' && !Number.isInteger(currentMargin) ? Number(currentMargin.toFixed(2)) : currentMargin}
                                        onChange={(e) => {
                                            setTempCost(null);
                                            const val = e.target.value;
                                            if (val !== '' && parseFloat(val) < minMargin) {
                                                setCurrentMargin(minMargin);
                                            } else {
                                                setCurrentMargin(val);
                                            }
                                        }}
                                        sx={{ width: 140, flexShrink: 0 }}
                                        InputProps={{
                                            endAdornment: <Typography variant="caption" sx={{ ml: 0.5, color: '#2e7d32', fontWeight: 600 }}>%</Typography>,
                                            sx: { bgcolor: '#fff', borderRadius: 1.5 }
                                        }}
                                        inputProps={{
                                            min: minMargin
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                {tripDetails?.location === "Sandakphu" && selectedPackage && (
                    <Box sx={{ mt: 1, p: 2, borderRadius: 1, border: '1px solid', borderColor: 'secondary.light', bgcolor: 'background.paper' }}>
                        <Typography variant="subtitle2" fontWeight={600} color="secondary.main" gutterBottom>
                            Sandakphu Package Calculation
                        </Typography>
                        {(() => {
                            const hasLandRover = selectedPackage?.label?.includes("Land Rover");
                            const head_count = (parseInt(tripDetails.pax) || 0) + (parseInt(tripDetails.kids_above_5) || 0);
                            const duration = parseInt(tripDetails.duration) || 0;

                            let subtotal = 0;
                            let breakdownItems = [];

                            if (hasLandRover) {
                                const hotelItemTotal = (parseFloat(hotelTotal) || 0) * head_count;
                                const carItemTotal = (parseFloat(carTotal) || 0);
                                subtotal = hotelItemTotal + carItemTotal;

                                breakdownItems.push({
                                    label: "Hotel Cost (Trip Total)",
                                    detail: `₹${(parseFloat(hotelTotal) || 0).toLocaleString()} × ${head_count} Pax`,
                                    value: hotelItemTotal
                                });
                                breakdownItems.push({
                                    label: "Car Cost (Trip Total)",
                                    detail: `Sandakphu Special Service`,
                                    value: carItemTotal
                                });
                            } else {
                                const singleCost = selectedPackage?.details?.cost?.singleCost || 0;
                                subtotal = singleCost * head_count;

                                breakdownItems.push({
                                    label: "Base Package Cost",
                                    detail: `₹${singleCost.toLocaleString()} × ${head_count} Pax`,
                                    value: subtotal
                                });
                            }

                            const discountPercent = parseFloat(currentMargin) || 0;
                            const discountAmount = subtotal * (discountPercent / 100);
                            const netBeforeMultiplier = subtotal - discountAmount;
                            const finalCost = netBeforeMultiplier * duration;

                            return (
                                <Box>
                                    {breakdownItems.map((item, idx) => (
                                        <Box key={idx} sx={{ mb: idx === breakdownItems.length - 1 ? 0 : 1 }}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={8}>
                                                    <Typography variant="body2">{item.label}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.detail}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4} textAlign="right">
                                                    <Typography variant="body2">₹{item.value.toLocaleString()}</Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    ))}

                                    <Divider sx={{ my: 1, opacity: 0.5 }} />

                                    <Grid container spacing={1}>
                                        <Grid item xs={8}>
                                            <Typography variant="body2" fontWeight={600}>Total Base</Typography>
                                        </Grid>
                                        <Grid item xs={4} textAlign="right">
                                            <Typography variant="body2" fontWeight={600}>₹{subtotal.toLocaleString()}</Typography>
                                        </Grid>
                                    </Grid>

                                    {discountAmount > 0 && (
                                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                            <Grid item xs={8}>
                                                <Typography variant="body2" color="error.main">Discount ({discountPercent}%)</Typography>
                                            </Grid>
                                            <Grid item xs={4} textAlign="right">
                                                <Typography variant="body2" color="error.main">- ₹{discountAmount.toLocaleString()}</Typography>
                                            </Grid>
                                        </Grid>
                                    )}

                                    <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

                                    <Grid container spacing={1}>
                                        <Grid item xs={8}>
                                            <Typography variant="body2">Rate After Discount</Typography>
                                        </Grid>
                                        <Grid item xs={4} textAlign="right">
                                            <Typography variant="body2">₹{netBeforeMultiplier.toLocaleString()}</Typography>
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                        <Grid item xs={8}>
                                            <Typography variant="body2" color="primary.main">Duration Multiplier</Typography>
                                            <Typography variant="caption" color="text.secondary">Applied per night factor</Typography>
                                        </Grid>
                                        <Grid item xs={4} textAlign="right">
                                            <Typography variant="body2" color="primary.main">× {duration}</Typography>
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ my: 1.5 }} />

                                    <Grid container spacing={1}>
                                        <Grid item xs={8}>
                                            <Typography variant="subtitle2" fontWeight={700}>Grand Total</Typography>
                                        </Grid>
                                        <Grid item xs={4} textAlign="right">
                                            <Typography variant="subtitle2" fontWeight={700} color="primary.dark">
                                                ₹{(Number(finalCost) || 0).toLocaleString()}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            );
                        })()}
                    </Box>
                )}
                {(tripDetails.optional_extras || []).length > 0 && (
                    <Box sx={{ mt: 1, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'secondary.light' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                p: 1,
                                bgcolor: 'secondary.light',
                                color: 'white',
                            }}
                        >
                            <Typography variant="body2" fontWeight={600}>
                                Optional Extras
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                ₹{extrasCost.toLocaleString()}
                            </Typography>
                        </Box>
                        <Box sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                            {tripDetails.optional_extras.map((extra, idx) => (
                                <Box key={idx} sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" sx={{ textDecoration: extra.include_in_cost === false ? 'line-through' : 'none' }}>
                                        {extra.name} {extra.count > 1 ? `(x${extra.count})` : ''} {extra.include_in_cost === false && <Typography component="span" variant="caption" sx={{ ml: 1, fontStyle: 'italic', color: '#ffd54f' }}>(Not included in total)</Typography>}
                                    </Typography>
                                    <Typography variant="caption" sx={{ textDecoration: extra.include_in_cost === false ? 'line-through' : 'none' }}>₹{((extra.price || 0) * (extra.count || 1)).toLocaleString()}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
                {tripDetails?.location !== "Sandakphu" && user?.permission === 'Admin' && carTotal > 0 && (
                    <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'primary.light' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                p: 1,
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText',
                                cursor: 'pointer',
                                transition: 'opacity 0.2s',
                                '&:hover': { opacity: 0.9 }
                            }}
                            onClick={() => setShowBreakdown(!showBreakdown)}
                        >
                            <Typography variant="body2" fontWeight={600}>Total Car Cost Included:</Typography>
                            <Typography variant="body2" fontWeight={600}>₹{(Number(carTotal) || 0).toLocaleString()}</Typography>
                        </Box>
                        <Collapse in={showBreakdown}>
                            <Box sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                                    Breakdown ({totalDays} Days - {carSeason === 'off_season_price' ? 'Off Season' : 'Season'})
                                </Typography>
                                {carSelections && Object.keys(carSelections).length > 0 ? (
                                    Object.keys(carSelections).map((dayIndex) => {
                                        const dayCars = carSelections[dayIndex];
                                        if (!Array.isArray(dayCars) || dayCars.length === 0) return null;
                                        return (
                                            <Box key={`day-${dayIndex}`} sx={{ mb: 1 }}>
                                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5, color: 'primary.main' }}>
                                                    Day {parseInt(dayIndex) + 1}
                                                </Typography>
                                                {dayCars.map((carDetail, index) => {
                                                    const carConfig = configData?.additionalCosts?.car?.find(c => c.type === carDetail.car_name);
                                                    let unitPrice = carConfig?.cost?.[carSeason] || 0;
                                                    
                                                    // (Removed Darjeeling hardcoded logic, use 'Darjeeling Bolero'/'Darjeeling Innova' car types instead)
                                                    
                                                    const total = unitPrice * carDetail.car_count;
                                                    return (
                                                        <Box key={index} sx={{ mb: 0.5, pl: 1 }}>
                                                            <Grid container spacing={1}>
                                                                <Grid item xs={7}>
                                                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                                                        {carDetail.car_name} (x{carDetail.car_count})
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={5} textAlign="right">
                                                                    <Typography variant="caption">₹{total.toLocaleString()}</Typography>
                                                                </Grid>
                                                            </Grid>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                                ₹{unitPrice.toLocaleString()} × {carDetail.car_count}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                                <Divider sx={{ my: 0.5, opacity: 0.5 }} />
                                            </Box>
                                        );
                                    })
                                ) : (
                                    carDetails.filter(car => car.car_count > 0).map((carDetail, index) => {
                                        const carConfig = configData?.additionalCosts?.car?.find(c => c.type === carDetail.car_name);
                                        let unitPrice = carConfig?.cost?.[carSeason] || 0;
                                        
                                        // (Removed Darjeeling hardcoded logic, use 'Darjeeling Bolero'/'Darjeeling Innova' car types instead)

                                        const total = unitPrice * carDetail.car_count * totalDays;
                                        return (
                                            <Box key={index} sx={{ mb: 0.5 }}>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={7}>
                                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                                            {carDetail.car_name} (x{carDetail.car_count})
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={5} textAlign="right">
                                                        <Typography variant="caption">₹{total.toLocaleString()}</Typography>
                                                    </Grid>
                                                </Grid>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                    ₹{unitPrice.toLocaleString()} × {carDetail.car_count} × {totalDays}
                                                </Typography>
                                                {index < carDetails.filter(car => car.car_count > 0).length - 1 && <Divider sx={{ my: 0.5, opacity: 0.5 }} />}
                                            </Box>
                                        );
                                    })
                                )}
                            </Box>
                        </Collapse>
                    </Box>
                )}
                {tripDetails?.location !== "Sandakphu" && user?.permission === 'Admin' && hotelTotal > 0 && (
                    <Box sx={{ mt: 1, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'info.light' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                p: 1,
                                bgcolor: 'info.light',
                                color: 'primary.contrastText',
                                cursor: 'pointer',
                                transition: 'opacity 0.2s',
                                '&:hover': { opacity: 0.9 }
                            }}
                            onClick={() => setShowHotelBreakdown(!showHotelBreakdown)}
                        >
                            <Typography variant="body2" fontWeight={600}>Total Hotel Cost Included:</Typography>
                            <Typography variant="body2" fontWeight={600}>₹{(Number(hotelTotal) || 0).toLocaleString()}</Typography>
                        </Box>
                        <Collapse in={showHotelBreakdown}>
                            <Box sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                                    Hotel Selection ({hotelSeason === 'off_season_price' ? 'Off Season' : 'Season'})
                                </Typography>
                                {Object.keys(hotelSelections).map((dayIndex, index) => {
                                    const dayData = hotelSelections[dayIndex];
                                    const arr = Array.isArray(dayData) ? dayData : (dayData ? [dayData] : []);
                                    return arr.map((selection, roomIdx) => {
                                        if (!selection?.hotelId) return null;
                                        const hotel = allHotels.find(h => h._id?.toString() === selection.hotelId?.toString());
                                        const roomCat = selection.roomType;
                                        const plan = selection.mealPlan?.split('_')[0].toUpperCase();

                                        const breakdown = calculateSingleHotelCostWithBreakdown(selection, allHotels, hotelSeason, tripDetails, stayInfo);

                                        return (
                                            <Box key={`${dayIndex}-${roomIdx}`} sx={{ mb: 1.5 }}>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={7}>
                                                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                                            Day {parseInt(dayIndex) + 1}: {hotel?.hotel_name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                                                            {roomCat} | {plan}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={5} textAlign="right">
                                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                            ₹{breakdown.total.toLocaleString()}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>

                                                {/* Breakdown Details */}
                                                <Box sx={{ mt: 0.5, pl: 1 }}>
                                                    {breakdown.baseCost > 0 && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6rem' }}>
                                                            • Base: ₹{breakdown.details.basePrice.toLocaleString()} × {breakdown.details.rooms} Rooms = ₹{breakdown.baseCost.toLocaleString()}
                                                        </Typography>
                                                    )}
                                                    {breakdown.extraAdultCost > 0 && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6rem' }}>
                                                            • Extra Mattress: ₹{breakdown.details.extraPrice.toLocaleString()} × {breakdown.details.extraAdults} Pax = ₹{breakdown.extraAdultCost.toLocaleString()}
                                                        </Typography>
                                                    )}
                                                    {breakdown.childCost > 0 && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6rem' }}>
                                                            • CNB: ₹{breakdown.details.childPrice.toLocaleString()} × {breakdown.details.kids} Kids = ₹{breakdown.childCost.toLocaleString()}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {(index < Object.keys(hotelSelections).length - 1 || roomIdx < arr.length - 1) && <Divider sx={{ mt: 1, opacity: 0.5 }} />}
                                            </Box>
                                        );
                                    });
                                })}
                            </Box>
                        </Collapse>
                    </Box>
                )}
            {/* End of IIFE wrapper for effective margin logic */}
            </Box>
                );
            })()}
        </Paper>
    );
};

export default CostEstimateCard;
